import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  onBackdrop: () => void;
  ariaLabel?: string;
  ariaLabelledby?: string;
  className?: string;
  dataTestId?: string;
  placement?: 'center' | 'bottom';
  restoreFocusFallback?: () => HTMLElement | null;
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], summary, [tabindex]:not([tabindex="-1"])';

/** Modal común: foco inicial, cierre nativo/Escape, focus trap, restauración y backdrop. */
export function ModalOverlay({
  children,
  onBackdrop,
  ariaLabel = 'Diálogo',
  ariaLabelledby,
  className = 'max-w-sm',
  dataTestId,
  placement = 'center',
  restoreFocusFallback,
}: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef(onBackdrop);
  closeRef.current = onBackdrop;
  const fallbackRef = useRef(restoreFocusFallback);
  fallbackRef.current = restoreFocusFallback;

  useEffect(() => {
    const Watcher = (
      window as Window & {
        CloseWatcher?: new () => EventTarget & { destroy(): void };
      }
    ).CloseWatcher;
    if (typeof Watcher !== 'function') return;
    const watcher = new Watcher();
    const close = () => closeRef.current();
    watcher.addEventListener('close', close);
    return () => {
      watcher.removeEventListener('close', close);
      watcher.destroy();
    };
  }, []);

  useEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    const focusable = dialog?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (focusable ?? dialog)?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
      const previous = previousFocusRef.current;
      if (previous?.isConnected && previous !== document.body) previous.focus();
      else (fallbackRef.current?.() ?? previous)?.focus();
    };
  }, []);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      onBackdrop();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [],
    );
    if (focusable.length === 0) {
      event.preventDefault();
      dialogRef.current?.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      className={`ui-modal-overlay fixed inset-0 z-50 flex justify-center bg-black/50 ${placement === 'bottom' ? 'items-end' : 'items-center p-4'}`}
      role="presentation"
      data-testid={dataTestId}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onBackdrop();
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={dialogRef}
        className={`ui-dialog w-full bg-surface p-4 shadow-lg ${placement === 'bottom' ? 'ui-bottom-sheet' : 'rounded-2xl border border-border'} ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabelledby ? undefined : ariaLabel}
        aria-labelledby={ariaLabelledby}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}
